import type { Locale as DateFnsLocale } from 'date-fns'
import { format } from 'date-fns'

import { dateFnsLanguageMap } from '@/constants/locales'
import { checkLangSupported } from '@/utils/intl'

type DateFnsFn<T extends any[] = any[]> = (...args: T) => any
type WrapperFunctionType = <T extends DateFnsFn>(
  fn: T,
  getLocale: () => DateFnsLocale | undefined,
  optionsIndex?: number
) => (...args: T extends DateFnsFn<infer U> ? U : any[]) => ReturnType<T>

const generateWrapperFunction: WrapperFunctionType =
  (fn, getLocale, optionsIndex = 2) =>
  (...args) => {
    if (!args[optionsIndex]) args[optionsIndex] = {}
    // Read the locale at call time so date-fns picks up the dynamically loaded
    // locale once it resolves (keeps computed values reactive to the locale ref).
    if (!args[optionsIndex].locale) args[optionsIndex].locale = getLocale()
    args[0] = args[0].toString() === 'Invalid Date' ? new Date() : args[0]
    return fn(...args)
  }

// Module-level cache of already-loaded locale data (static per locale, so it is
// safe to share across SSR requests). Lets a mounted component reuse a locale
// synchronously instead of flashing null -> locale on every mount.
const loadedLocales: Record<string, DateFnsLocale> = {}

export function useI18nDateFns() {
  const { locale: i18nLocale } = useI18n()

  const dateFnsLocale = ref<DateFnsLocale | null>(null)

  watch(i18nLocale, updateDateFnsLocale, { immediate: true })

  async function updateDateFnsLocale(newLocale: string) {
    if (!checkLangSupported(newLocale)) {
      // null lets date-fns fall back to its built-in en-US default.
      console.warn(`[useI18nDateFns] locale "${newLocale}" not supported, falling back to date-fns built-in en-US`)
      dateFnsLocale.value = null
      return
    }

    if (loadedLocales[newLocale]) {
      dateFnsLocale.value = loadedLocales[newLocale]
      return
    }

    try {
      const localeModule = await dateFnsLanguageMap[newLocale]()
      loadedLocales[newLocale] = localeModule.default

      // Guard against out-of-order resolution if the locale changed again mid-load.
      if (i18nLocale.value === newLocale) {
        dateFnsLocale.value = localeModule.default
      }
    } catch (error: any) {
      // null lets date-fns fall back to its built-in en-US default.
      console.warn(`[useI18nDateFns] locale "${newLocale}" load failed, falling back to date-fns built-in en-US, error: ${error.message}`)

      if (i18nLocale.value === newLocale) {
        dateFnsLocale.value = null
      }
    }
  }

  return {
    dateFnsLocale,
    format: generateWrapperFunction(format, () => dateFnsLocale.value ?? undefined),
  }
}
